import {
  createAdminTermsCategory,
  deleteAdminTerms,
  getAdminTerms,
  getAdminTermsCategories,
  type LegalTerms,
  mapClientError,
  queryKeys,
} from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  LoadingOverlay,
  modals,
  notifications,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TitleContainer,
  Tooltip,
  useModal,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Edit2, Eye, ListFilter, Plus, Settings, Tag, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TermsCategoryForm } from "./components/TermsCategoryForm";
import { TermsPreviewContent } from "./components/TermsPreviewContent";

export function TermsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 목록 필터 및 페이징 상태
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string>("all");

  // 모달 상태
  const previewModal = useModal();

  // 1. 카테고리 목록 조회
  const { data: categories } = useQuery({
    queryKey: queryKeys.terms.categories(),
    queryFn: getAdminTermsCategories,
  });

  // 2. 약관 목록 페이징 및 필터 조회
  const {
    data: termsData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: queryKeys.terms.adminList(page, limit, selectedCategoryKey),
    queryFn: () =>
      getAdminTerms({
        page,
        limit,
        categoryKey: selectedCategoryKey === "all" ? undefined : selectedCategoryKey,
      }),
  });

  // 3. 카테고리 추가 mutation
  const createCategoryMutation = useMutation({
    mutationFn: createAdminTermsCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.categories() });
      modals.close("add-category-modal");
    },
    onError: (error: unknown) => {
      notifications.show({
        color: "red",
        title: "등록 실패",
        message: mapClientError(error, { fallback: "카테고리 생성에 실패했습니다." }).message,
      });
    },
  });

  // 4. 약관 삭제 mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAdminTerms,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.terms.all });
    },
  });

  const handleCreateCategory = (values: { key: string; name: string; description: string }) => {
    createCategoryMutation.mutate({
      key: values.key.trim(),
      name: values.name.trim(),
      description: values.description.trim(),
    });
  };

  const openCategoryAddModal = () => {
    modals.open({
      modalId: "add-category-modal",
      title: "신규 약관 구분 추가",
      closeOnClickOutside: false,
      radius: "lg",
      styles: { title: { fontWeight: 700 } },
      children: (
        <Stack gap="md">
          <TermsCategoryForm formId="create-category-form" onSubmit={handleCreateCategory} />
          <Group justify="flex-end" mt="md">
            <Button variant="light" color="gray" onClick={() => modals.close("add-category-modal")}>
              취소
            </Button>
            <Button
              type="submit"
              form="create-category-form"
              loading={createCategoryMutation.isPending}
            >
              추가 등록
            </Button>
          </Group>
        </Stack>
      ),
    });
  };

  const handleDelete = (id: string) => {
    if (confirm("정말 이 임시저장 약관을 삭제하시겠습니까?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleOpenPreviewModal = (item: LegalTerms) => {
    previewModal.open({
      modalId: "terms-preview-modal",
      title: `${item.title} (v${item.version}) 미리보기`,
      size: "lg",
      children: <TermsPreviewContent terms={item} modalId="terms-preview-modal" />,
    });
  };

  // 카테고리 필터링 Select 데이터 구성
  const categoryFilterData = [
    { value: "all", label: "전체 구분 보기" },
    ...(categories?.map((c) => ({ value: c.key, label: c.name })) || []),
  ];

  const termsList = termsData?.list || [];
  const total = termsData?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <TitleContainer
      title="약관 관리"
      rightSection={
        <Group gap="sm">
          <Button
            variant="light"
            color="blue"
            leftSection={<Tag size={16} />}
            onClick={openCategoryAddModal}
            radius="md"
          >
            약관 구분 추가
          </Button>
          <Button
            leftSection={<Plus size={16} />}
            onClick={() => navigate("/terms/new")}
            radius="md"
          >
            새 약관 발행
          </Button>
        </Group>
      }
    >
      <Stack gap="lg">
        {/* 필터 제어 영역 */}
        <Card withBorder radius="lg" shadow="xs" p="md">
          <Group justify="space-between" align="flex-end">
            <Group gap="md">
              <Select
                label={
                  <Group gap={4} align="center">
                    <ListFilter size={14} />
                    <Text size="xs" fw={700}>
                      약관 구분 필터
                    </Text>
                  </Group>
                }
                data={categoryFilterData}
                value={selectedCategoryKey}
                onChange={(val) => {
                  setSelectedCategoryKey(val || "all");
                  setPage(1); // 필터 교체 시 첫 페이지로 리셋
                }}
                style={{ width: 220 }}
              />
            </Group>

            <Group gap="xs">
              <Text size="xs" c="dimmed">
                페이지 당 표시 개수:
              </Text>
              <Select
                data={[
                  { value: "10", label: "10개씩 보기" },
                  { value: "20", label: "20개씩 보기" },
                  { value: "50", label: "50개씩 보기" },
                ]}
                value={limit.toString()}
                onChange={(val) => {
                  setLimit(parseInt(val || "10", 10));
                  setPage(1);
                }}
                style={{ width: 130 }}
              />
            </Group>
          </Group>
        </Card>

        {/* 목록 테이블 */}
        <Card withBorder radius="lg" shadow="sm" pos="relative" style={{ overflow: "hidden" }}>
          <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />
          <Table highlightOnHover withTableBorder verticalSpacing="sm">
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th style={{ width: 100 }}>상태</Table.Th>
                <Table.Th style={{ width: 180 }}>약관 구분 (Category)</Table.Th>
                <Table.Th style={{ width: 100 }}>구분 여부</Table.Th>
                <Table.Th style={{ width: 100 }}>버전</Table.Th>
                <Table.Th>약관명</Table.Th>
                <Table.Th style={{ width: 160 }}>릴리즈 일자</Table.Th>
                <Table.Th style={{ width: 140 }}>작성자</Table.Th>
                <Table.Th style={{ width: 140 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={8} align="center" py="xl">
                    <Stack align="center" gap="xs">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        약관 목록을 불러오는 중입니다...
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ) : termsList.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={8} align="center" py="xl">
                    <Text c="dimmed" size="sm">
                      조건에 맞는 약관이 존재하지 않습니다.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                termsList.map((item: LegalTerms) => {
                  const isDraft = item.releasedAt === null;
                  return (
                    <Table.Tr
                      key={item.id}
                      style={
                        item.isActive
                          ? { backgroundColor: "var(--mantine-color-blue-0)" }
                          : undefined
                      }
                    >
                      <Table.Td>
                        {item.isActive ? (
                          <Badge color="blue" variant="filled">
                            발행중
                          </Badge>
                        ) : isDraft ? (
                          <Badge color="yellow" variant="light">
                            임시저장
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="outline">
                            비활성
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={6}>
                          <Settings size={14} color="var(--mantine-color-gray-5)" />
                          <Text size="sm" fw={500}>
                            {item.category?.name || item.categoryKey}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={item.isRequired ? "red" : "gray"} variant="light">
                          {item.isRequired ? "필수" : "선택"}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text fw={600} size="sm">
                          {item.version}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={item.isActive ? 600 : 400}>
                          {item.title}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">
                          {isDraft ? "-" : dayjs(item.releasedAt).format("YYYY-MM-DD HH:mm")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={item.creator?.email || ""}>
                          <Text size="xs" style={{ cursor: "pointer" }}>
                            {item.creator?.name || "시스템"}
                          </Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={8} justify="flex-end">
                          <ActionIcon
                            variant="light"
                            color="gray"
                            size="sm"
                            onClick={() => handleOpenPreviewModal(item)}
                            title="미리보기"
                          >
                            <Eye size={14} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            color="blue"
                            size="sm"
                            onClick={() => navigate(`/terms/${item.id}/edit`)}
                            title="수정"
                          >
                            <Edit2 size={14} />
                          </ActionIcon>
                          {!item.isActive && isDraft && (
                            <ActionIcon
                              variant="light"
                              color="red"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              title="삭제"
                            >
                              <Trash2 size={14} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>

          {/* 페이징 컴포넌트 연동 */}
          {totalPages > 1 && (
            <Group justify="center" py="md">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                radius="md"
                withEdges
              />
            </Group>
          )}
        </Card>
      </Stack>
    </TitleContainer>
  );
}
